const KEY_TOKEN = 'token';
const KEY_REFRESH_TOKEN = 'refreshToken';
const KEY_REFRESH_ENDPOINT = 'refreshEndpoint';
const KEY_CM_TOKEN = 'cmToken';
const KEY_CM_REFRESH_TOKEN = 'cmRefreshToken';
// Identifies one login (not one token: access and refresh tokens are renewed during a login). New on every real sign-in
// (setSession), removed on sign-out (clearSession), the same in every tab and after a reload of the same login.
const KEY_LOGIN_ID = 'loginSessionId';

const newLoginId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const TokenStorage = {
  setSession(token: string, refreshToken: string | null, refreshEndpoint: string): void {
    try {
      localStorage.setItem(KEY_TOKEN, token);
      if (refreshToken) {
        localStorage.setItem(KEY_REFRESH_TOKEN, refreshToken);
      } else {
        localStorage.removeItem(KEY_REFRESH_TOKEN);
      }
      localStorage.setItem(KEY_REFRESH_ENDPOINT, refreshEndpoint);
      localStorage.setItem(KEY_LOGIN_ID, newLoginId());
    } catch (e) {
      console.error('TokenStorage.setSession failed', e);
    }
  },

  // Sign-in paths that only receive an access token (registration, Facebook, phone PIN, invitations): the token format
  // is unchanged (plain localStorage 'token'), but it is a new login, so the login id rotates like in setSession.
  // Token renewals must keep using updateAccessToken / updateTokens, which never rotate it.
  setTokenAndRotateLogin(token: string): void {
    try {
      localStorage.setItem(KEY_TOKEN, token);
      localStorage.setItem(KEY_LOGIN_ID, newLoginId());
    } catch (e) {
      console.error('TokenStorage.setTokenAndRotateLogin failed', e);
    }
  },

  // true in a tab that works under another user's identity ("login as another user"): its token is in sessionStorage.
  isImpersonating(): boolean {
    try {
      return Boolean(sessionStorage.getItem(KEY_TOKEN));
    } catch {
      return false;
    }
  },

  // Impersonated (in-shoes) sessions live in sessionStorage instead of the shared localStorage -
  // each tab opened for an in-shoes switch gets its own copy, so switching identity in a freshly
  // opened tab can never silently swap the token the admin's own original tab is using for its own
  // requests (localStorage is shared across every same-origin tab and read fresh on every API call
  // - see RestApiService._createRequestHeader). Real login always goes through setSession, which is
  // unaffected.
  setImpersonatedSession(token: string): void {
    try {
      sessionStorage.setItem(KEY_TOKEN, token);
    } catch (e) {
      console.error('TokenStorage.setImpersonatedSession failed', e);
    }
  },

  getToken(): string | null {
    try {
      return sessionStorage.getItem(KEY_TOKEN) ?? localStorage.getItem(KEY_TOKEN);
    } catch {
      return null;
    }
  },

  // The id of the current login, or null when storage is unavailable or nobody is signed in. A sign-in path that does
  // not go through setSession gets its id on first use; clearSession (sign-out) always removes it, so the next sign-in
  // is a new login either way.
  getLoginId(): string | null {
    try {
      const existing = localStorage.getItem(KEY_LOGIN_ID);
      if (existing) return existing;
      if (!TokenStorage.getToken()) return null;
      const created = newLoginId();
      localStorage.setItem(KEY_LOGIN_ID, created);
      return created;
    } catch {
      return null;
    }
  },

  getRefreshToken(): string | null {
    try {
      // A tab holding its own session-scoped (impersonated) token never refreshes - falling
      // through to localStorage's refresh token here would silently refresh using the admin's own
      // real refresh token from inside an impersonated tab. Matches the existing "in-shoes tokens
      // are 8h only, no refresh" rule, just tab-scoped instead of global.
      if (TokenStorage.isImpersonating()) return null;
      return localStorage.getItem(KEY_REFRESH_TOKEN);
    } catch {
      return null;
    }
  },

  getRefreshEndpoint(): string | null {
    try {
      return localStorage.getItem(KEY_REFRESH_ENDPOINT);
    } catch {
      return null;
    }
  },

  updateAccessToken(token: string): void {
    try {
      localStorage.setItem(KEY_TOKEN, token);
    } catch (e) {
      console.error('TokenStorage.updateAccessToken failed', e);
    }
  },

  updateTokens(token: string, refreshToken: string | null): void {
    try {
      localStorage.setItem(KEY_TOKEN, token);
      if (refreshToken) {
        localStorage.setItem(KEY_REFRESH_TOKEN, refreshToken);
      } else {
        localStorage.removeItem(KEY_REFRESH_TOKEN);
      }
    } catch (e) {
      console.error('TokenStorage.updateTokens failed', e);
    }
  },

  clearSession(): void {
    try {
      localStorage.removeItem(KEY_TOKEN);
      localStorage.removeItem(KEY_REFRESH_TOKEN);
      localStorage.removeItem(KEY_REFRESH_ENDPOINT);
      localStorage.removeItem(KEY_LOGIN_ID);
      // Also clears this tab's own impersonated session, if any - otherwise a logout from inside
      // an in-shoes tab would leave getToken() still returning the stale sessionStorage token.
      sessionStorage.removeItem(KEY_TOKEN);
    } catch (e) {
      console.error('TokenStorage.clearSession failed', e);
    }
  },

  setCMSession(token: string, refreshToken: string | null): void {
    try {
      localStorage.setItem(KEY_CM_TOKEN, token);
      if (refreshToken) {
        localStorage.setItem(KEY_CM_REFRESH_TOKEN, refreshToken);
      } else {
        localStorage.removeItem(KEY_CM_REFRESH_TOKEN);
      }
    } catch (e) {
      console.error('TokenStorage.setCMSession failed', e);
    }
  },

  getCMToken(): string | null {
    try {
      return localStorage.getItem(KEY_CM_TOKEN);
    } catch {
      return null;
    }
  },

  getCMRefreshToken(): string | null {
    try {
      return localStorage.getItem(KEY_CM_REFRESH_TOKEN);
    } catch {
      return null;
    }
  },

  clearCMSession(): void {
    try {
      localStorage.removeItem(KEY_CM_TOKEN);
      localStorage.removeItem(KEY_CM_REFRESH_TOKEN);
    } catch (e) {
      console.error('TokenStorage.clearCMSession failed', e);
    }
  },
};
